import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getContentByVersionPath } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickableLinkProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { preventOrphan } from "@SupplyLand/Utilities/richText";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    showImages = "showImages",
    showOnlyTopLevelCategories = "showOnlyTopLevelCategories",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImages]: boolean;
        [fields.showOnlyTopLevelCategories]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const getCategoriesParameter = {
        maxDepth: props.fields.showOnlyTopLevelCategories ? 1 : 2,
        includeStartCategory: false,
        startCategoryId: "",
    };
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname === getContentByVersionPath
            ? parseQueryString<{ categoryPath: string }>(location.search).categoryPath
            : "") ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    const topCategoryLabel = catalogPageState.value?.properties["topCategoryLabel"];
    const supportingCategoryLabel = catalogPageState.value?.properties["supportingCategoryLabel"];


    return {
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
        category: getCategoryState(
            state,
            catalogPageState.value?.categoryIdWithBrandId ?? catalogPageState.value?.categoryId,
        ).value,
        state,
        topCategoryLabel,
        supportingCategoryLabel,
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface CategoryListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    topContainer?: GridContainerProps;
    topWrapper?: InjectableCss;
    categoryItem?: GridItemProps;
    supportingCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    supportingCategoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    topCategoryImage?: LazyImageProps;
    supportCategoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    supportingCategoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    supportingCategoryNameLink?: LinkPresentationProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkPresentationProps;
    topCategoriesHeader?: InjectableCss;
    supportCategoriesHeader?: InjectableCss;
    topCategoryName?: TypographyProps;
    categoryNotes?: InjectableCss;
    topCategoryNotes?: InjectableCss;
    topCategoryNotesWrapper?: InjectableCss;
    topCategoriesHeaderWrapper?: InjectableCss;
    topCategoriesWrapper?: InjectableCss;
    supportContainer?: GridContainerProps;
    topCategoryNotesLinks?: ClickableLinkProps;
    topCategoryNotesTitle?: TypographyProps;
    topCategoryNotesText?: TypographyProps;
    categoryImageBannerItem?: GridItemProps;
    categoryImageBanner?: TypographyProps;
    categoryListLeftImage?: InjectableCss;
    categoryListRightImage?: InjectableCss;
}

export const categoryListStyles: CategoryListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    topContainer: {
        gap: 40,
        css: css`
            padding: 75px 20px;
            max-width: 100%;
        `,
    },
    topWrapper: {
        css: css`
            background-color: #e9eaec;
        `,
    },
    supportContainer: {
        gap: 20,
    },
    categoryItem: {
        width: [12, 12, 6, 4, 4],
    },
    supportingCategoryItem: {
        width: [12, 6, 4, 3, 3],
    },
    innerContainer: {
        gap: 0,
    },
    categoryImageItem: {
        width: 12,
        css: css`
            height: 250px;
            text-align: center;
            display: block;
            margin-bottom: 40px;
        `,
    },
    supportingCategoryImageItem: {
        width: 12,
        css: css`
            height: 200px;
            text-align: center;
            display: block;
            margin-bottom: 40px;
        `,
    },
    topCategoryImage: {
        width: "250px",
        height: "250px;",
    },
    supportCategoryImage: {
        width: "200px",
        height: "200px",
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            text-align: center;
            display: block;
            background: black;
            border-top-left-radius: 20px;
            border-top-right-radius: 20px;
            max-width: 250px;
            margin: auto !important;
        `,
    },
    supportingCategoryNameLinkItem: {
        width: 12,
        css: css`
            text-align: center;
            display: block;
            max-width: 200px;
            padding: 5px 0;
            margin: auto !important;
        `,
    },
    categoryNameLink: {
        typographyProps: {
            variant: "h3",
            css: css`
                margin-bottom: 0;
            `,
        },
        color: "white",
        css: css`
            width: 100%;
            padding: 20px 0;
            font-weight: 700;
            font-size: 17px;
            justify-content: center;
        `,
    },
    supportingCategoryNameLink: {
        typographyProps: {
            variant: "h3",
            css: css`
                margin-bottom: 0;
            `,
        },
        color: "black",
        css: css`
            width: 100%;
            font-weight: 700;
            font-size: 17px;
            justify-content: center;
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
    },
    viewMoreLinkItem: {
        width: 12,
        css: css`
            font-weight: bold;
            padding-top: 10px;
        `,
    },
    topCategoriesHeader: {
        css: css`
            justify-content: center;
            text-transform: uppercase;
            align-items: center;
            font-size: 28px;
            font-weight: 700;
            line-height: 1.2;
            -moz-border-radius: 15px; //for mozilla support
            -webkit-border-radius: 15px; //for chrome support
            background: rgb(255, 255, 255);
            padding-left: 40px;
            padding-right: 40px;
            color: rgba(0, 0, 0, 0.94);
            border-radius: 40px;
            padding-top: 36px;
            padding-bottom: 20px;
            margin-bottom: -40px;
            margin-top: 30px;
            width: 100%;
            max-width: 800px;
            text-align: center;
        `,
    },
    topCategoriesHeaderWrapper: {
        css: css`
            align-items: center;
            display: flex;
            justify-content: center;
        `,
    },
    supportCategoriesHeader: {
        css: css`
            justify-content: center;
            text-transform: uppercase;
            align-items: center;
            font-size: 28px;
            font-weight: 700;
            line-height: 1.2;
            -moz-border-radius: 15px; //for mozilla support
            -webkit-border-radius: 15px; //for chrome support
            background: rgb(255, 255, 255);
            padding-left: 40px;
            padding-right: 40px;
            color: rgba(0, 0, 0, 0.94);
            border-radius: 40px;
            padding-top: 12px;
            padding-bottom: 20px;
            margin-top: -40px;
            margin-bottom: 30px;
            width: 100%;
            max-width: 800px;
            text-align: center;
        `,
    },
    categoryNotes: {
        css: css`
            width: 100%;
            background: #ffd100;
            text-transform: uppercase;
            height: 100px;
            font-weight: 700;
            padding-top: 20px;
            margin-top: 84px;
            text-align: center;
            margin-top: 94px;
        `,
    },
    topCategoryNotesWrapper: {
        css: css`
            align-items: center;
            display: flex;
            justify-content: center;
            position: relative;
        `,
    },
    topCategoryNotes: {
        css: css`
            text-transform: uppercase;
            font-size: 17px;
            font-weight: 800;
            -moz-border-bottom-left-radius: 40px; //for mozilla support
            -webkit-border-bottom-left-radius: 40px; //for chrome support
            -moz-border-bottom-right-radius: 40px; //for mozilla support
            -webkit-border-bottom-right-radius: 40px; //for chrome support
            -moz-border-top-right-radius: 0px; //for mozilla support
            -webkit-border-top-right-radius: 0px; //for chrome support
            -moz-border-top-left-radius: 0px; //for mozilla support
            -webkit-border-top-left-radius: 0px; //for chrome support
            background: rgb(0, 0, 0);
            padding-left: 40px;
            padding-right: 40px;
            color: rgba(250, 250, 250, 0.94);
            border-bottom-left-radius: 40px;
            border-top-left-radius: 0px;
            border-top-right-radius: 0px;
            border-bottom-right-radius: 40px;
            padding-top: 40px;
            padding-bottom: 5px;
            height: 300px;
            text-align: center;
            z-index: 2;
        `,
    },
    topCategoriesWrapper: {
        css: css`
            background-color: #e9eaec;
        `,
    },
    topCategoryNotesLinks: {
        css: css`
            color: white;
            font-weight: 400;
            font-size: 13px;
            text-decoration: none;
            justify-content: center;
        `,
    },
    topCategoryNotesTitle: {
        css: css`
            text-decoration: underline;
            justify-content: center;
            margin-bottom: 50px;
            display: block;
        `,
    },
    topCategoryNotesText: {
        css: css`
            color: white;
            font-weight: 700;
            font-size: 13px;
            text-decoration: none;
            justify-content: center;
            margin-top: 30px;
            display: block;
        `,
    },
    categoryImageBannerItem: {
        width: 12,
        align: "middle",
        css: css`
            text-align: center;
            display: block;
            max-width: 250px;
            background: rgba(255, 255, 255, 0.5);
            z-index: 1;
            margin: -65px auto auto auto !important;
            font-weight: 700;
            padding: 10px;
        `,
    },
    categoryListLeftImage: {
        css: css`
            height: 300px;
            z-index: 1;

            img {
                height: 90%;
                width: auto;
                object-fit: contain;
            }

            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: block;
                        left: 0;
                        height: 23vw;
                    `,
                    css`
                        display: block;
                        left: 0;
                        height: 23vw;
                    `,
                    css`
                        display: block;
                    `,
                ]);
            }}
        `,
    },
    categoryListRightImage: {
        css: css`
            height: 300px;
            width: auto;
            z-index: 1;

            img {
                height: 90%;
                width: auto;
                object-fit: contain;
            }

            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: block;
                        right: 0;
                        height: 23vw;
                    `,
                    css`
                        display: block;
                        right: 0;
                        height: 23vw;
                    `,
                    css`
                        display: block;
                    `,
                ]);
            }}
        `,
    },
};

const styles = categoryListStyles;

class CategoryList extends React.Component<Props> {
    container = React.createRef<HTMLDivElement>();

    UNSAFE_componentWillMount(): void {
        const { getCategoriesParameter, category, state, loadCategories } = this.props;
        if (category) {
            getCategoriesParameter.startCategoryId = category.id;
            const categoriesDataView = getCategoriesDataView(state, getCategoriesParameter);
            if (!categoriesDataView.value) {
                loadCategories(getCategoriesParameter);
            }
        }
    }

    render() {
        const {
            category,
            fields: { showImages, showOnlyTopLevelCategories },
            state,
            getCategoriesParameter,
            topCategoryLabel,
            supportingCategoryLabel,
        } = this.props;

        if (category) {
            getCategoriesParameter.startCategoryId = category.id;
        }

        const categoriesDataView = getCategoriesDataView(state, getCategoriesParameter);

        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value || !category) {
            return null;
        }

        let subCategoryIds = category.subCategoryIds ? category.subCategoryIds : [];

        const categories = categoriesDataView.value.filter(o => subCategoryIds.indexOf(o.id) > -1);

        const topCategoryNotesText = "All curated. All easy. All delivered with a wink.";

        let topCategoriesCount = 3;
        if (category.parentId === null) {
            topCategoriesCount = 6;
        }
        
        const topCategoriesHeader = topCategoryLabel || translate("Top Categories");
        const supportingCategoriesHeader = supportingCategoryLabel || translate("Supporting Categories");

        return (
            <StyledWrapper ref={this.container}>
                <StyledWrapper {...styles.topCategoriesHeaderWrapper}>
                    <StyledWrapper {...styles.topCategoriesHeader}>
                        <Typography as="h2">{preventOrphan(topCategoriesHeader)}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.topWrapper}>
                    <GridContainer {...styles.topContainer}>
                        {categories.slice(0, topCategoriesCount).map(category => (
                            <GridItem key={category.id.toString()} {...styles.categoryItem}>
                                <GridContainer {...styles.innerContainer}>
                                    <GridItem {...styles.categoryNameLinkItem}>
                                        <Link href={category.path} {...styles.categoryNameLink}>
                                            {category.name}
                                        </Link>
                                    </GridItem>
                                    {showImages && (
                                        <GridItem {...styles.categoryImageItem}>
                                            {category.path && (
                                                <Link href={category.path} {...styles.categoryImageLink}>
                                                    <LazyImage
                                                        src={category.smallImagePath}
                                                        {...styles.topCategoryImage}
                                                        altText={category.imageAltText}
                                                    />
                                                </Link>
                                            )}
                                        </GridItem>
                                    )}

                                    {/* {!showOnlyTopLevelCategories && (
                                        <>
                                            {category.subCategoryIds &&
                                                category.subCategoryIds.slice(0, 4).map(subCategoryId => (
                                                    <GridItem key={subCategoryId} {...styles.subCategoryNameLinkItem}>
                                                        <SubCategoryLink categoryId={subCategoryId} />
                                                    </GridItem>
                                                ))}
                                            {category.subCategoryIds && category.subCategoryIds.length > 4 && (
                                                <GridItem {...styles.viewMoreLinkItem}>
                                                    <Link href={category.path} {...styles.viewMoreLink}>
                                                        {translate("View More")}
                                                    </Link>
                                                </GridItem>
                                            )}
                                        </>
                                    )} */}
                                    {category.properties["imageBanner"] && (
                                        <GridItem {...styles.categoryImageBannerItem}>
                                            <Typography {...styles.categoryImageBanner}>
                                                {category.properties["imageBanner"]}
                                            </Typography>
                                        </GridItem>
                                    )}
                                </GridContainer>
                            </GridItem>
                        ))}
                    </GridContainer>
                </StyledWrapper>

                {categories.length > 3 && (
                    <>
                        <StyledWrapper {...styles.topCategoriesHeaderWrapper}>
                            <StyledWrapper {...styles.supportCategoriesHeader}>
                                <Typography as="h2">{preventOrphan(supportingCategoriesHeader)}</Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                        <GridContainer {...styles.supportContainer}>
                            {categories.slice(topCategoriesCount).map(category => (
                                <GridItem key={category.id.toString()} {...styles.supportingCategoryItem}>
                                    <GridContainer {...styles.innerContainer}>
                                        {showImages && (
                                            <GridItem {...styles.supportingCategoryImageItem}>
                                                {category.path && (
                                                    <Link href={category.path} {...styles.categoryImageLink}>
                                                        <LazyImage
                                                            src={category.smallImagePath}
                                                            {...styles.supportCategoryImage}
                                                            altText={category.imageAltText}
                                                        />
                                                    </Link>
                                                )}
                                            </GridItem>
                                        )}
                                        <GridItem {...styles.supportingCategoryNameLinkItem}>
                                            <Link href={category.path} {...styles.supportingCategoryNameLink}>
                                                {category.name}
                                            </Link>
                                        </GridItem>
                                        {/* {!showOnlyTopLevelCategories && (
                                    <>
                                        {category.subCategoryIds &&
                                            category.subCategoryIds.slice(0, 4).map(subCategoryId => (
                                                <GridItem key={subCategoryId} {...styles.subCategoryNameLinkItem}>
                                                    <SubCategoryLink categoryId={subCategoryId} />
                                                </GridItem>
                                            ))}
                                        {category.subCategoryIds && category.subCategoryIds.length > 4 && (
                                            <GridItem {...styles.viewMoreLinkItem}>
                                                <Link href={category.path} {...styles.viewMoreLink}>
                                                    {translate("View More")}
                                                </Link>
                                            </GridItem>
                                        )}
                                    </>
                                )} */}
                                    </GridContainer>
                                </GridItem>
                            ))}
                        </GridContainer>
                    </>
                )}

                <StyledWrapper {...styles.categoryNotes}>
                    {category.properties["bottomContent"] && (
                        <Typography data-test-selector={`categoryName_${category.name}`}>
                            {parse(category.properties["bottomContent"])}
                        </Typography>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.topCategoryNotesWrapper}>
                    <StyledWrapper {...styles.categoryListLeftImage}>
                        <img
                            src="/UserFiles/categoryimages/category-list-image-left.png"
                            alt="Category decoration left"
                        />
                    </StyledWrapper>

                    <StyledWrapper {...styles.topCategoryNotes}>
                        <Typography {...styles.topCategoryNotesTitle}>
                            {translate("Top Categories Include:")}
                        </Typography>
                        {categories.slice(0, 3).map(category => (
                            <Clickable
                                key={category.id.toString()}
                                href={category.path}
                                {...styles.topCategoryNotesLinks}
                            >
                                {category.name}
                            </Clickable>
                        ))}
                        <Typography {...styles.topCategoryNotesText}>{topCategoryNotesText}</Typography>
                    </StyledWrapper>

                    <StyledWrapper {...styles.categoryListRightImage}>
                        <img
                            src="/UserFiles/categoryimages/category-list-image-right.png"
                            alt="Category decoration right"
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const SubCategoryLinkView = ({ category }: ReturnType<typeof linkMapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link href={category.path} {...styles.subCategoryNameLink}>
            {category.name}
        </Link>
    );
};

const linkMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(linkMapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CategoryList)),
    definition: {
        group: "Categories",
        allowedContexts: ["CategoryListPage", "CategoryDetailsPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showOnlyTopLevelCategories,
                displayName: "Show only top-level categories",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
